import type { MenuLink } from '../MenuSetting';
import type { IconNames } from '@workramp/ui';
import {
  Modal,
  ModalHeader,
  ModalBody,
  TextField,
  AutocompleteField,
  Checkbox,
  ModalFooter,
  Button,
  Stack,
  IconButton,
} from '@workramp/ui';
import { useCallback, useState } from 'react';
import { useHubTheming } from '~/clc/features/theming/providers/useHubTheming';

interface MenuLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEdit: (link: MenuLink) => void;
  onCreate: (link: MenuLink) => void;
  linkToEdit?: MenuLink;
  predefinedLinks: MenuLink[];
  externalBaseUrl?: string;
}

const DEFAULT_ICON: IconNames = 'link:regular';

export const MenuLinkModal = ({
  isOpen,
  onClose,
  linkToEdit,
  predefinedLinks = [],
  onEdit,
  onCreate,
  externalBaseUrl,
}: MenuLinkModalProps) => {
  const isEditMode = Boolean(linkToEdit);
  const hubTheme = useHubTheming();
  const {
    constants: { externalBaseUrl: externalBaseUrlFromTheme },
  } = hubTheme;

  const [label, setLabel] = useState(() => linkToEdit?.label || '');
  const [selectedHref, setSelectedHref] = useState(() => linkToEdit?.href || '');
  const [customHref, setCustomHref] = useState(() => linkToEdit?.href || '');
  const [openInNewTab, setOpenInNewTab] = useState(() => linkToEdit?.openInNewTab || false);

  const [iconName, setIconName] = useState<IconNames>(() => {
    if (isEditMode) {
      const predefinedLink = predefinedLinks.find((link) => link.href === linkToEdit.href);
      return predefinedLink?.iconName || DEFAULT_ICON;
    }
    return DEFAULT_ICON;
  });

  const [autoCompleteOptions, setAutoCompleteOptions] = useState<MenuLink[]>(() => {
    if (isEditMode) {
      const predefinedLink = predefinedLinks.some((link) => link.href === linkToEdit.href);
      if (!predefinedLink) {
        return [...predefinedLinks, { ...linkToEdit, iconName: DEFAULT_ICON }];
      }
    }
    return predefinedLinks;
  });

  const formatHref = useCallback(
    (href: string): string => {
      const baseUrl = externalBaseUrl || externalBaseUrlFromTheme;

      // Predefined link
      if (predefinedLinks.some((link) => link.href === href)) return href;

      // Relative link
      if (href.includes(baseUrl)) {
        const startIndex = href.indexOf(baseUrl);
        return href.substring(startIndex + baseUrl.length);
      }

      // Special protocols
      if (href.startsWith('mailto:') || href.startsWith('tel:')) return href;

      // Already has protocol
      if (/^https?:\/\//.test(href)) return href;

      // Add https protocol
      return `https://${href}`;
    },
    [externalBaseUrl, externalBaseUrlFromTheme, predefinedLinks],
  );

  const handleSave = useCallback(() => {
    const saveAction = isEditMode ? onEdit : onCreate;
    const predefinedLink = predefinedLinks.find((link) => link.href === selectedHref);

    saveAction({
      label,
      openInNewTab,
      iconName: predefinedLink?.iconName || DEFAULT_ICON,
      href: formatHref(predefinedLink?.href || customHref),
    });
  }, [isEditMode, onEdit, onCreate, predefinedLinks, selectedHref, label, openInNewTab, customHref, formatHref]);

  const handleSelectionChange = useCallback(
    (href: string) => {
      const predefinedLink = predefinedLinks.find((link) => link.href === href);
      setSelectedHref(predefinedLink?.href || href);
      setIconName(predefinedLink?.iconName || DEFAULT_ICON);
    },
    [predefinedLinks],
  );

  const handleInputChange = useCallback(
    (value: string | null = null) => {
      if (!value) {
        setCustomHref('');
        setAutoCompleteOptions(predefinedLinks);
        return;
      }

      // Filter predefined links by label
      const filteredLinks = predefinedLinks.filter((link) => link.label.toLowerCase().includes(value.toLowerCase()));

      // Create custom link option
      const isAbsoluteLink = /^(https?:\/\/|mailto:|tel:)/.test(value) || value.startsWith('/');
      const href = isAbsoluteLink ? value : `https://${value}`;

      setAutoCompleteOptions([
        ...filteredLinks,
        {
          label: value,
          href,
          iconName: DEFAULT_ICON,
        },
      ]);

      setCustomHref(href);
    },
    [predefinedLinks],
  );

  const handleClearForm = useCallback(() => {
    setSelectedHref('');
    setCustomHref('');
    setIconName(DEFAULT_ICON);
  }, []);

  const isValidLink = label.trim() !== '' && (!!selectedHref || !!customHref);

  return (
    <Modal isOpen={isOpen} size="sm" onClose={onClose}>
      <ModalHeader title={isEditMode ? 'Edit menu item' : 'Create menu item'} />
      <ModalBody>
        <Stack size={4}>
          <TextField label="Name" value={label} onChange={setLabel} />

          <AutocompleteField
            allowsCustomValue
            showTrailingButton={selectedHref !== ''}
            menuTrigger="focus"
            trailingButtonEl={
              <IconButton
                aria-label="Clear link"
                color="secondary"
                iconName="x-small:regular"
                label="Clear"
                size="sm"
                variant="text"
                onPress={handleClearForm}
              />
            }
            leadingIconName={iconName}
            hideLabel
            label="Link"
            placeholder="Paste a link or search"
            items={autoCompleteOptions}
            selectedKey={selectedHref}
            onInputChange={handleInputChange}
            onSelectionChange={handleSelectionChange}
          >
            {(item: MenuLink) => (
              <AutocompleteField.Item key={item.href} iconName={item.iconName}>
                {item.label}
              </AutocompleteField.Item>
            )}
          </AutocompleteField>

          <Checkbox isSelected={openInNewTab} onChange={setOpenInNewTab}>
            Open as a new tab
          </Checkbox>
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Button isBlock onPress={handleSave} isDisabled={!isValidLink}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};
